/**
 * 统一的API Client
 * 处理所有后端API请求
 */

import { logger } from '../utils/logger';

export interface ApiConfig {
  baseUrl: string;
  timeout?: number;
}

export interface ApiResponse<T = any> {
  data?: T;
  error?: string;
  status: number;
}

export class ApiClient {
  private baseUrl: string;
  private timeout: number;
  private token: string | null = null;

  constructor(config: ApiConfig) {
    this.baseUrl = config.baseUrl;
    this.timeout = config.timeout || 10000;
    
    // ✅ 统一端口架构：优先从 current_session 获取 token
    const sessionStr = localStorage.getItem('current_session');
    if (sessionStr) {
      try {
        const session = JSON.parse(sessionStr);
        if (session.token) {
          this.token = session.token;
          return;
        }
      } catch {
        // 解析失败，继续尝试 auth_token
      }
    }
    
    // ✅ 兜底：从 auth_token 获取（兼容旧版本）
    this.token = localStorage.getItem('auth_token');
  }

  /**
   * 设置认证Token
   */
  setToken(token: string | null): void {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
      console.log('[ApiClient] ✅ Token 已保存到 localStorage');
    } else {
      localStorage.removeItem('auth_token');
      console.log('[ApiClient] ❌ Token 已从 localStorage 移除');
    }
  }

  /**
   * 获取当前Token
   */
  getToken(): string | null {
    // 优先使用内存中的token
    if (this.token) {
      return this.token;
    }
    
    // ✅ 统一端口架构：优先从 current_session 获取 token
    const sessionStr = localStorage.getItem('current_session');
    console.log('[ApiClient] 🔍 getToken() 被调用，sessionStr:', sessionStr ? '存在' : '不存在');
    if (sessionStr) {
      try {
        const session = JSON.parse(sessionStr);
        console.log('[ApiClient] 🔍 解析 session 成功:', { hasToken: !!session.token });
        if (session.token) {
          console.log('[ApiClient] 🔄 从 current_session 恢复 token');
          this.token = session.token;
          return session.token;
        } else {
          console.warn('[ApiClient] ⚠️ session 中没有 token 字段');
        }
      } catch (error) {
        console.warn('[ApiClient] ⚠️ current_session 解析失败:', error);
      }
    }
    
    // ✅ 兜底：尝试从 auth_token 恢复（兼容旧版本）
    const storedToken = localStorage.getItem('auth_token');
    if (storedToken) {
      console.log('[ApiClient] 🔄 从 auth_token 恢复 token');
      this.token = storedToken;
      return storedToken;
    }
    
    console.warn('[ApiClient] ⚠️ Token 不存在');
    return null;
  }

  /**
   * 通用请求方法
   */
  private async request<T = any>(
    method: string,
    path: string,
    data?: any,
    options?: RequestInit
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${path}`;
    
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options?.headers,
    };

    // 添加认证Token
    if (this.token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.token}`;
    }

    const config: RequestInit = {
      method,
      headers,
      ...options,
    };

    if (data && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
      config.body = JSON.stringify(data);
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(url, {
        ...config,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const responseData = await response.json();

      if (!response.ok) {
        return {
          error: responseData.error || `HTTP ${response.status}`,
          status: response.status,
        };
      }

      return {
        data: responseData,
        status: response.status,
      };
    } catch (error: any) {
      logger.error(`API请求失败: ${method} ${path}`, error);
      
      if (error.name === 'AbortError') {
        return {
          error: '请求超时',
          status: 408,
        };
      }

      return {
        error: error.message || '网络错误',
        status: 0,
      };
    }
  }

  /**
   * GET请求
   */
  async get<T = any>(path: string, options?: RequestInit): Promise<ApiResponse<T>> {
    return this.request<T>('GET', path, undefined, options);
  }

  /**
   * POST请求
   */
  async post<T = any>(path: string, data?: any, options?: RequestInit): Promise<ApiResponse<T>> {
    return this.request<T>('POST', path, data, options);
  }

  /**
   * PUT请求
   */
  async put<T = any>(path: string, data?: any, options?: RequestInit): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', path, data, options);
  }

  /**
   * DELETE请求
   */
  async delete<T = any>(path: string, options?: RequestInit): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', path, undefined, options);
  }
}

// 创建默认实例
export const apiClient = new ApiClient({
  baseUrl: '',
  timeout: 30000,
});

